import { useState, useMemo, useCallback } from 'react';

import type { UIEvent } from 'react';

const DOUBLE = 2;

interface UseVirtualScrollProps {
  rowHeight: number;
  buffer?: number;
  totalItems: number;
}

export function useVirtualScroll({ rowHeight, buffer = DOUBLE, totalItems }: UseVirtualScrollProps) {
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  const { startIndex, endIndex } = useMemo(() => {
    if (rowHeight <= 0) {
      return { startIndex: 0, endIndex: totalItems - 1 };
    }

    const visibleItemsCount = Math.ceil(containerHeight / rowHeight);
    const start = Math.max(0, Math.floor(scrollTop / rowHeight) - buffer);
    const end = Math.min(totalItems - 1, start + visibleItemsCount + buffer * DOUBLE);
    return { startIndex: start, endIndex: end };
  }, [scrollTop, rowHeight, totalItems, containerHeight, buffer]);

  const totalHeight = totalItems * rowHeight;

  const offsetTop = startIndex * rowHeight;

  const visibleItems = useMemo(
    () => Array.from({ length: endIndex - startIndex + 1 }, (_, counter) => startIndex + counter),
    [startIndex, endIndex]
  );

  const onScroll = useCallback((event: UIEvent<HTMLElement>) => {
    setScrollTop(event.currentTarget.scrollTop);
  }, []);

  return {
    visibleItems,
    totalHeight,
    offsetTop,
    onScroll,
    setContainerHeight,
  };
}
